import { Link } from "@tanstack/react-router";

import { facilityLabel, normalizeFacilities } from "../../lib/facilities";
import { regionName, useI18n } from "../../lib/i18n";
import { priceUnit } from "../../lib/place";
import type { Club, HomeClubRow } from "../../lib/api/lidoo.functions";
import { coverSources, imageKind } from "../../lib/place-photos";
import { ClubPlate } from "./club-plate";
import { CoverImage } from "./cover-image";
import { GoogleRating, RatingSummary } from "./reviews";

// "from EUR 70 / 2 sunbeds with parasol, per day". The unit matters: our 147
// published rates cover different things (one bed, a bed with a parasol, a pair
// with an umbrella, a day pass), so an amount without a unit quietly compares
// prices that are not comparable. The club's own longer wording is not dropped,
// it moves to the detail line on the club page.
export function priceLine(club: Club, t: (k: string) => string): string {
  if (club.price_from == null) return t("price_on_request");
  const symbol = club.currency === "EUR" ? "EUR " : `${club.currency} `;
  const { short } = priceUnit(club.price_unit, t);
  return `${t("from")} ${symbol}${club.price_from}${short ? ` / ${short}` : ""}`;
}

// A short, scannable line of what the place actually has. Three items and a
// count of the rest: our facility data is unevenly filled, and "Bar · Pool ·
// Showers · +13" makes a well-documented club look rich without making a
// thinly-documented one look broken.
function facilityLine(club: Club, t: (k: string) => string): string | null {
  const keys = normalizeFacilities(club.facilities, club.amenities);
  if (!keys.length) return null;
  const shown = keys.slice(0, 3).map((f) => facilityLabel(f, t));
  const rest = keys.length - shown.length;
  return shown.join(" · ") + (rest > 0 ? ` · +${rest}` : "");
}

// The club's own photo wins (uploaded, supplied with permission, or freely
// licensed: those we may keep); a Google Places photo carries the card
// otherwise; the drawn plate is the last resort. The order itself is decided in
// lib/place-photos.ts so the card, the homepage and the club page cannot drift
// apart. Cards ask for the 400px variant, which is all a 3-column grid needs.
export function coverImage(club: Club): string | null {
  return coverSources(club, 400)[0]?.src ?? null;
}

// What the small label on the picture says. A licensed photo is of the beach
// near the club, and the card says so before any credit ("Beach near Vilamoura
// · Photo: Jane Doe / CC BY-SA 4.0"); a CC0 one carries the note alone. An own
// photo only ever shows a credit, and normally has none.
export function coverLabel(
  club: { image: string | null; image_credit: string | null; town: string },
  t: (k: string, v?: Record<string, string>) => string,
): string | null {
  if (!club.image) return null;
  const credit = club.image_credit ? t("photo_credit", { name: club.image_credit }) : null;
  if (imageKind(club.image) !== "licensed") return credit;
  const nearby = t("photo_nearby", { place: club.town });
  return credit ? `${nearby} · ${credit}` : nearby;
}

export function ClubCard({ club, distanceKm }: { club: Club; distanceKm?: number | null }) {
  const { t } = useI18n();
  const facilities = facilityLine(club, t);
  const hasPrice = club.price_from != null;

  return (
    <Link
      to="/club/$slug"
      params={{ slug: club.slug }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-[#1C2430]/10 bg-white/70 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#1C2430]/20 hover:shadow-[0_18px_40px_-24px_rgba(28,36,48,0.45)] active:translate-y-0"
    >
      <div className="relative h-52 overflow-hidden">
        <CoverImage
          sources={coverSources(club, 400)}
          alt={club.name}
          altByKind={{ licensed: t("photo_nearby", { place: club.town }) }}
          width={400}
          height={208}
          credit={coverLabel(club, t)}
          fallback={<ClubPlate club={club} />}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
        {/* Only the badge that earns its place: "visited by us" is a real
            signal, "open listing" is the default and just adds noise. */}
        {club.verified ? (
          <span className="li-mono absolute left-3 top-3 rounded-full bg-[#2C4FD6] px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#F2EFE9]">
            {t("verified")}
          </span>
        ) : null}
        {club.sponsored ? (
          <span className="li-mono absolute right-3 top-3 rounded-full bg-[#1C2430] px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#F2EFE9]">
            {t("sponsored_badge")}
          </span>
        ) : hasPrice ? (
          /* Only 118 of 794 clubs publish a rate. Saying so on the card turns
             the thing that makes us different into something you can see at a
             glance, and it is simply true of this listing. */
          <span className="li-mono absolute right-3 top-3 rounded-full bg-[#F2EFE9]/95 px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#1C2430]">
            {t("badge_price_shown")}
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="li-serif text-[1.35rem] leading-tight text-[#1C2430]">{club.name}</h3>
          {typeof distanceKm === "number" ? (
            <span className="li-mono mt-1 shrink-0 text-[11px] text-[#2C4FD6]">
              {distanceKm.toFixed(distanceKm < 10 ? 1 : 0)} {t("km_away")}
            </span>
          ) : null}
        </div>

        <p className="mt-1 text-sm text-[#5B6472]">
          {club.town}, {regionName(club.region_key, club.region_label, t)}
        </p>

        {/* Ratings sit directly under the location, which is where a traveler
            looks first. Ours leads when we have it; Google's carries the card
            in the far more common case that we don't yet. */}
        {club.review_count > 0 || club.google_rating != null ? (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <RatingSummary avg={club.avg_rating} count={club.review_count} />
            <GoogleRating rating={club.google_rating} count={club.google_review_count} />
          </div>
        ) : null}

        {facilities ? (
          <p className="mt-3 line-clamp-1 text-[13px] text-[#5B6472]">{facilities}</p>
        ) : null}

        {/* Price sits on its own footer line, pushed to the bottom so a row of
            cards lines its prices up regardless of how long the names run. */}
        <div className="mt-auto flex items-baseline gap-2 pt-4">
          <span
            className={`li-mono text-[13px] ${hasPrice ? "font-medium text-[#1C2430]" : "text-[#5B6472]"}`}
          >
            {priceLine(club, t)}
          </span>
        </div>
      </div>
    </Link>
  );
}

// A card for rows that only ever need the handful of fields a browsing visitor
// scans: picture, name, place, rating, price. It exists so the homepage can
// stop shipping the entire club record, description and all, for every listing
// it shows.
export function MiniClubCard({ club }: { club: HomeClubRow }) {
  const { t } = useI18n();
  const unit = priceUnit(club.price_unit, t).short;
  const price =
    club.price_from == null
      ? t("price_on_request")
      : `${t("from")} ${club.currency === "EUR" ? "EUR " : `${club.currency} `}${club.price_from}${
          unit ? ` / ${unit}` : ""
        }`;

  return (
    <Link
      to="/club/$slug"
      params={{ slug: club.slug }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-[#1C2430]/10 bg-white/70 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#1C2430]/20 hover:shadow-[0_18px_40px_-24px_rgba(28,36,48,0.45)]"
    >
      <div className="relative h-52 overflow-hidden bg-[#E7E2D6]">
        <CoverImage
          sources={coverSources(club, 400)}
          alt={club.name}
          altByKind={{ licensed: t("photo_nearby", { place: club.town }) }}
          width={400}
          height={208}
          credit={coverLabel(club, t)}
          fallback={<ClubPlate club={club} />}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="li-serif text-[1.35rem] leading-tight text-[#1C2430]">{club.name}</h3>
        <p className="mt-1 text-sm text-[#5B6472]">
          {club.town}, {regionName(club.region_key, club.region_label, t)}
        </p>
        {club.google_rating != null ? (
          <div className="mt-2">
            <GoogleRating rating={club.google_rating} count={club.google_review_count} />
          </div>
        ) : null}
        <div className="mt-auto pt-4">
          <span
            className={`li-mono text-[13px] ${
              club.price_from != null ? "font-medium text-[#1C2430]" : "text-[#5B6472]"
            }`}
          >
            {price}
          </span>
        </div>
      </div>
    </Link>
  );
}
